/** Talaffuz qadami (demo `step` → speak): Web Speech `SpeechRecognition`; mikrofon bo'lmasa `listen` (yoki `recUz`) ga almashadi. */
import { useEffect, useRef, useState } from 'react';
import { lev, LN, norm } from '../../../data';
import { t } from '../../../i18n';
import { canSpeak, setSrOk, speak, SR_OK, SRClass, type SpeechRecognitionLike } from '../../../lib/speech';
import { Ic } from '../../../ui/Icon';
import { replaceStep, useLesson, type Step } from '../engine';
import { answer, Frame, Prompt } from './common';

const NOTE = 'Bu sahifada mikrofon ishlamaydi, shuning uchun talaffuz eshitib tanlash orqali tekshiriladi. Mobil ilovada ovozingiz tekshiriladi.';

/** Mikrofon ishlamasa — eshitib tanlash (ovoz ham bo'lmasa — variantlar bilan). */
function fallback() {
  if (!canSpeak()) { replaceStep({ t: 'recUz' }); return; }
  const st = useLesson.getState();
  replaceStep({ t: 'listen', note: st.L?.noteShown ? '' : NOTE });
  st.set({ noteShown: true });
}

export function SpeakStep({ s }: { s: Step }) {
  const w = s.w!;
  const broken = !SRClass() || SR_OK === false;
  const [on, setOn] = useState(false);
  const [status, setStatus] = useState('Bosing va gapiring');
  const rec = useRef<SpeechRecognitionLike | null>(null);
  const tm = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => { if (broken) fallback(); }, [broken]);
  useEffect(() => () => { try { rec.current?.abort(); } catch { /* ignore */ } clearTimeout(tm.current); }, []);
  if (broken) return null;

  const listen = () => {
    const SR = SRClass(); if (!SR) { setSrOk(false); fallback(); return; }
    let r: SpeechRecognitionLike;
    try { r = new SR(); } catch { setSrOk(false); fallback(); return; }
    rec.current = r;
    r.lang = LN.tts; r.maxAlternatives = 5; r.interimResults = false;
    setOn(true); setStatus('Tinglayapman…');
    let done = false;
    r.onresult = (e) => {
      done = true;
      const alts = Array.from(e.results[0]!).map((x) => norm(x.transcript)); const tt = norm(w.w);
      const ok = alts.some((a) => a === tt || a.split(' ').includes(tt) || lev(a, tt) <= 1);
      setSrOk(true); setOn(false);
      const full = t(`Toʻgʻrisi: ${w.w} ${w.ipa}. Bu soʻz shu bosqich oxirida yana chiqadi.`), k = full.indexOf(w.w);
      answer(w, ok, ok ? {} : {
        head: t('Yana urinib koʻring'),
        ans: <span style={{ fontSize: 18, fontWeight: 700 }}>{t(`Siz aytdingiz: “${alts[0] || '…'}”`)}</span>,
        msg: k >= 0 ? <>{full.slice(0, k)}<b>{w.w}</b>{full.slice(k + w.w.length)}</> : full,
      });
    };
    r.onerror = (e) => {
      done = true; setOn(false);
      if (['not-allowed', 'service-not-allowed', 'audio-capture', 'network'].includes(e.error)) { setSrOk(false); fallback(); }
      else setStatus('Eshitilmadi. Yana bosib, aniqroq ayting');
    };
    r.onend = () => { setOn(false); if (!done) setStatus('Eshitilmadi. Yana bosib, aniqroq ayting'); };
    try { r.start(); } catch { setSrOk(false); fallback(); return; }
    clearTimeout(tm.current);
    tm.current = setTimeout(() => {
      if (!done) { try { r.abort(); } catch { /* ignore */ } setOn(false); setStatus('Eshitilmadi. Mikrofon ruxsatini tekshiring yoki “Hozir gapira olmayman”ni bosing'); }
    }, 7000);
  };

  return (
    <Frame foot={<button className="btn soft" id="skip" onClick={() => replaceStep({ t: 'listen' })}>{t('Hozir gapira olmayman')}</button>}>
      <Prompt retry={s.retry} text="Soʻzni ovoz chiqarib ayting" />
      <div className="qcard" style={{ alignItems: 'center', textAlign: 'center', padding: '16px 16px 22px' }}>
        <b style={{ fontSize: 34, lineHeight: '42px' }}>{w.w}</b>
        <span className="t13" style={{ fontSize: 15 }}>{w.ipa} · {w.uz}</span>
        <button className="btn soft" id="au" style={{ height: 44, width: 'auto', padding: '0 16px', marginTop: 10, gap: 8 }} onClick={() => speak(w.w)}><Ic n="vol" style={{ width: 18, height: 18 }} />{t('Namunani tinglash')}</button>
      </div>
      <div className="col g10" style={{ alignItems: 'center' }}>
        <button className={`mic${on ? ' on' : ''}`} id="mic" aria-label={t('Gapirish')} onClick={listen}><Ic n="mic" style={{ width: 34, height: 34 }} /></button>
        <span className="t13" id="st">{t(status)}</span>
      </div>
    </Frame>
  );
}
